/**
 * MerchU CRO module.
 *
 * <mu-upsell-item>     one-click add for upsell rows (cart drawer, cart page, product page).
 * <mu-card-buy>        product-card color dots + tap-a-size-to-add.
 * <mu-bundle-tiers>    (legacy) product-page quantity tier picker; replaced by <mu-bundle-builder>.
 * <mu-welcome-popup>   welcome-offer email capture.
 *
 * Every add goes through addLines(), which dispatches the same CartLinesUpdateEvent the theme's own
 * product form uses, so the cart drawer / cart page re-render themselves and auto-open as configured.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const COOKIE_NAME = 'mu_popup';

/** Grid lists that already have a re-render observer for color cards. */
const expandedLists = new WeakSet();

/** @param {string} name */
function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * @param {string} name
 * @param {string} value
 * @param {number} days
 */
function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * Adds lines to the cart and lets the theme's cart components re-render.
 * @param {HTMLElement} source - Element the event is dispatched from.
 * @param {Array<{ id: number, quantity: number }>} items
 * @param {'product' | 'cart'} context
 */
async function addLines(source, items, context) {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : undefined))
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Could not add to cart');

    const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const cart = await cartResponse.json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: data.sections,
        source: 'mu-cro',
        itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * Shared button feedback: spinner → "Added" → original label.
 * @param {HTMLButtonElement} button
 * @param {() => Promise<void>} work
 * @param {(message: string) => void} onError
 */
async function runWithFeedback(button, work, onError) {
  if (button.disabled || button.classList.contains('is-loading')) return;
  const label = button.querySelector('.mu-cb__label, .mu-upsell__label');
  const original = label?.textContent ?? '';
  button.classList.add('is-loading');
  button.setAttribute('aria-busy', 'true');
  try {
    await work();
    button.classList.remove('is-loading');
    button.classList.add('is-added');
    if (label) label.textContent = button.dataset.addedLabel || '✓';
    window.setTimeout(() => {
      button.classList.remove('is-added');
      if (label) label.textContent = original;
    }, 1600);
  } catch (error) {
    button.classList.remove('is-loading');
    onError(error instanceof Error ? error.message : 'Something went wrong');
  } finally {
    button.removeAttribute('aria-busy');
  }
}

/** @param {HTMLElement} host @param {string} message @param {string} className */
function showError(host, message, className) {
  host.querySelector(`.${className}`)?.remove();
  const p = document.createElement('p');
  p.className = className;
  p.setAttribute('role', 'alert');
  p.textContent = message;
  host.appendChild(p);
  window.setTimeout(() => p.remove(), 5000);
}

/* ------------------------------------------------------------------ */
/* Upsell rows                                                         */
/* ------------------------------------------------------------------ */

class MuUpsellItem extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const button = this.querySelector('[data-mu-add]');
    button?.addEventListener('click', (event) => this.#add(event), { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {Event} event */
  #add(event) {
    const button = /** @type {HTMLButtonElement | null} */ (event.currentTarget);
    if (!button) return;

    const variantInput = /** @type {HTMLInputElement | HTMLSelectElement | null} */ (
      this.querySelector('.mu-upsell__variant')
    );
    const variantId = Number(variantInput?.value);
    if (!variantId) return;

    /** @type {Array<{ id: number, quantity: number }>} */
    const items = [{ id: variantId, quantity: 1 }];

    if (this.dataset.addMain === 'true') {
      const mainInput = /** @type {HTMLInputElement | null} */ (
        document.querySelector('product-form-component input[name="id"]')
      );
      const mainId = Number(mainInput?.value);
      if (mainId && mainId !== variantId) items.unshift({ id: mainId, quantity: 1 });
    }

    button.dataset.addedLabel = 'Added';
    const context = this.closest('.product-information, product-information') ? 'product' : 'cart';
    runWithFeedback(button, () => addLines(this, items, context), (message) =>
      showError(this, message, 'mu-upsell__error')
    );
  }
}

/* ------------------------------------------------------------------ */
/* Product-card quick buy                                              */
/* ------------------------------------------------------------------ */

/**
 * @typedef {{ id: number, a: boolean, c: string | null, s: string | null, m: number | null }} CardVariant
 */

class MuCardBuy extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {CardVariant[]} */
  #variants = [];
  /** @type {string | null} */
  #color = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      this.#variants = JSON.parse(this.querySelector('script[data-mu-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    const checked = this.querySelector('.mu-cb__swatch[aria-checked="true"]');
    this.#color = checked instanceof HTMLElement ? checked.dataset.color ?? null : null;

    // Color cards: a cloned card starts on its own color; an original card spawns one card per extra color.
    if (this.dataset.clone) {
      const swatch = [...this.querySelectorAll('.mu-cb__swatch')].find(
        (s) => s instanceof HTMLButtonElement && s.dataset.color === this.dataset.clone
      );
      if (swatch instanceof HTMLButtonElement) queueMicrotask(() => this.#selectColor(swatch));
    } else if (this.dataset.colorCards === 'true') {
      queueMicrotask(() => this.#expandColors());
    }

    this.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const swatch = target.closest('.mu-cb__swatch');
        if (swatch instanceof HTMLButtonElement) {
          event.preventDefault();
          this.#selectColor(swatch);
          return;
        }

        const add = target.closest('[data-mu-cb-add]');
        if (add instanceof HTMLButtonElement) {
          event.preventDefault();
          this.#add(add);
        }
      },
      { signal }
    );

    // Arrow-key movement between color dots (radiogroup pattern)
    this.querySelector('.mu-cb__colors')?.addEventListener(
      'keydown',
      (event) => {
        if (!(event instanceof KeyboardEvent)) return;
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        const swatches = [...this.querySelectorAll('.mu-cb__swatch')];
        const current = swatches.indexOf(/** @type {Element} */ (document.activeElement));
        if (current < 0) return;
        event.preventDefault();
        const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
        const next = /** @type {HTMLButtonElement} */ (swatches[(current + step + swatches.length) % swatches.length]);
        next.focus();
        this.#selectColor(next);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {HTMLButtonElement} swatch */
  #selectColor(swatch) {
    const color = swatch.dataset.color ?? null;
    if (!color) return;
    this.#color = color;

    for (const s of this.querySelectorAll('.mu-cb__swatch')) {
      s.setAttribute('aria-checked', s === swatch ? 'true' : 'false');
    }
    const name = this.querySelector('[data-color-name]');
    if (name) name.textContent = color;

    // Re-point size chips at this color's variants
    for (const chip of this.querySelectorAll('.mu-cb__size')) {
      if (!(chip instanceof HTMLButtonElement)) continue;
      const variant = this.#variants.find((v) => v.c === color && v.s === chip.dataset.size);
      if (variant) chip.dataset.variantId = String(variant.id);
      else delete chip.dataset.variantId;
      chip.disabled = !variant || !variant.a;
    }

    // Color-only products: retarget the quick add button
    const quick = this.querySelector('.mu-cb__quick');
    if (quick instanceof HTMLButtonElement) {
      const variant = this.#variants.find((v) => v.c === color && v.a) ?? this.#variants.find((v) => v.c === color);
      if (variant) quick.dataset.variantId = String(variant.id);
      quick.disabled = !variant?.a;
    }

    const lead = this.#variants.find((v) => v.c === color && v.a) ?? this.#variants.find((v) => v.c === color);
    // Swap the card photo even when this color has no photo of its own (falls back to the main photo)
    this.#showMedia(lead?.m ?? null);
    if (lead) {
      const card = /** @type {any} */ (this.closest('product-card'));
      try {
        if (typeof card?.applyVariantToLinks === 'function') {
          card.applyVariantToLinks(String(lead.id));
        } else {
          for (const a of card?.querySelectorAll('a[href]') ?? []) {
            if (!(a instanceof HTMLAnchorElement) || !a.getAttribute('href')?.includes('/products/')) continue;
            const url = new URL(a.href, window.location.origin);
            url.searchParams.set('variant', String(lead.id));
            a.href = url.toString();
          }
        }
      } catch {
        /* card links stay on the product URL */
      }
    }
  }

  /**
   * One card per in-stock color: clones this card's grid item for every other color and marks the
   * clone so it starts on that color. Idempotent, so it can re-run after the grid re-renders.
   */
  #expandColors() {
    const li = this.closest('li');
    if (!li || li.closest('slideshow-slide') || !li.parentElement) return;
    const list = li.parentElement;
    const pid = this.closest('product-card')?.getAttribute('data-product-id') || '';
    if (!pid) return;

    /** @type {string[]} */
    const colors = [];
    for (const v of this.#variants) {
      if (v.c && v.c !== this.#color && v.a && !colors.includes(v.c)) colors.push(v.c);
    }
    if (!colors.length) return;

    let anchor = li;
    colors.forEach((color, i) => {
      const key = `${pid}:${color}`;
      const existing = [...list.children].find((el) => el.getAttribute('data-mu-color-card') === key);
      if (existing) {
        anchor = existing;
        return;
      }
      const clone = /** @type {HTMLElement} */ (li.cloneNode(true));
      clone.setAttribute('data-mu-color-card', key);
      for (const el of clone.querySelectorAll('[id]')) el.id = `${el.id}-mu${i + 1}`;
      clone.querySelector('product-card')?.removeAttribute('view-event-payload');
      const host = clone.querySelector('mu-card-buy');
      if (host instanceof HTMLElement) {
        host.removeAttribute('data-color-cards');
        host.setAttribute('data-clone', color);
      }
      anchor.after(clone);
      anchor = clone;
    });

    // Filters / infinite scroll re-render the list: re-expand anything that lost its clones.
    if (!expandedLists.has(list)) {
      expandedLists.add(list);
      let timer = 0;
      new MutationObserver(() => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          for (const h of list.querySelectorAll('mu-card-buy[data-color-cards="true"]')) {
            /** @type {any} */ (h).expandColors?.();
          }
        }, 50);
      }).observe(list, { childList: true });
    }
  }

  /** Public hook for the list observer. */
  expandColors() {
    this.#expandColors();
  }

  /**
   * Shows the slide for a media id and hides the rest. Works directly on the slide elements
   * because the theme's card slideshow never initialises its scroller when only one slide is
   * visible at load, so slideshow.select() is a no-op there.
   * @param {number | null} mediaId - null falls back to the product's main (non-variant) photo.
   */
  #showMedia(mediaId) {
    const card = this.closest('product-card');
    const slideshow = /** @type {any} */ (card?.querySelector('slideshow-component'));
    if (!slideshow) return;
    const slides = /** @type {HTMLElement[]} */ ([...slideshow.querySelectorAll('slideshow-slide')]);
    if (!slides.length) return;

    let target = mediaId ? slides.find((s) => s.getAttribute('slide-id') === String(mediaId)) : undefined;
    if (!target) target = slides.find((s) => !s.hasAttribute('variant-image')) ?? slides[0];
    if (!target) return;

    for (const slide of slides) {
      const show = slide === target;
      slide.hidden = !show;
      if (show) {
        slide.setAttribute('reveal', '');
        slide.setAttribute('aria-hidden', 'false');
        slide.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
      } else {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // If the slideshow did initialise (carousel cards), keep its index in sync too.
    try {
      slideshow.select?.({ id: target.getAttribute('slide-id') }, undefined, { animate: false });
    } catch {
      /* the direct hide/show above already did the job */
    }
  }

  /** @param {HTMLButtonElement} button */
  #add(button) {
    const id = Number(button.dataset.variantId);
    if (!id || button.disabled) return;
    button.dataset.addedLabel = button.classList.contains('mu-cb__size') ? '✓' : 'Added';
    runWithFeedback(button, () => addLines(this, [{ id, quantity: 1 }], 'product'), (message) =>
      showError(this, message, 'mu-cb__error')
    );
  }
}

/* ------------------------------------------------------------------ */
/* Product-page bundle tiers (legacy, kept so old sections don't break) */
/* ------------------------------------------------------------------ */

class MuBundleTiers extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener(
      'click',
      (event) => {
        const row = event.target instanceof Element ? event.target.closest('.mu-tiers__row') : null;
        if (!(row instanceof HTMLElement)) return;
        event.preventDefault();
        this.#select(row);
      },
      { signal }
    );

    document.addEventListener(
      'input',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.getAttribute('ref') !== 'quantityInput') return;
        if (!input.closest('product-form-component')) return;
        this.#highlight(Number(input.value) || 1);
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  /** @param {HTMLElement} row */
  #select(row) {
    const qty = Number(row.dataset.qty) || 1;
    this.#highlight(qty);

    const selector = /** @type {any} */ (
      document.querySelector('product-form-component quantity-selector-component')
    );
    const input = /** @type {HTMLInputElement | null} */ (
      document.querySelector('product-form-component input[ref="quantityInput"]')
    );
    if (selector?.setValue) selector.setValue(String(qty));
    else if (input) input.value = String(qty);
    selector?.updateButtonStates?.();
  }

  /** @param {number} qty */
  #highlight(qty) {
    const rows = [...this.querySelectorAll('.mu-tiers__row')];
    let active = rows[0];
    for (const row of rows) {
      if (row instanceof HTMLElement && qty >= (Number(row.dataset.qty) || 1)) active = row;
    }
    for (const row of rows) row.setAttribute('aria-checked', row === active ? 'true' : 'false');
  }
}

/* ------------------------------------------------------------------ */
/* Restock alert                                                       */
/* ------------------------------------------------------------------ */

/** @typedef {{ a: boolean, s: string | null, t: string }} RestockVariant */

class MuRestock extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Record<string, RestockVariant>} */
  #variants = {};

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    try {
      this.#variants = JSON.parse(this.querySelector('script[data-rs-variants]')?.textContent || '{}');
    } catch {
      this.#variants = {};
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('restock') === 'done') {
      this.setAttribute('data-done', '');
      const done = this.querySelector('[data-rs-done]');
      if (done instanceof HTMLElement) done.hidden = false;
      const details = this.querySelector('details');
      if (details instanceof HTMLDetailsElement) details.open = true;
      // Tidy the URL so a refresh doesn't keep showing "done"
      try {
        params.delete('restock');
        params.delete('customer_posted');
        const clean = `${window.location.pathname}${params.size ? `?${params}` : ''}`;
        window.history.replaceState({}, '', clean);
      } catch {
        /* leave the URL alone */
      }
    }

    // Keep the form error state visible if Shopify bounced back with one
    if (this.querySelector('.mu-rs__error')) {
      const details = this.querySelector('details');
      if (details instanceof HTMLDetailsElement) details.open = true;
    }

    const sync = () => window.setTimeout(() => this.#sync(), 0);
    document.addEventListener('variant:update', sync, { signal });
    document.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof Element && event.target.closest('variant-picker, product-form-component')) sync();
      },
      { signal }
    );
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
  }

  #sync() {
    const input = /** @type {HTMLInputElement | null} */ (
      document.querySelector('product-form-component input[name="id"]')
    );
    const v = input ? this.#variants[input.value] : undefined;
    const allSoldOut = this.dataset.allSoldOut === 'true';
    const soldOut = allSoldOut || (v ? !v.a : false);
    const size = v?.s || '';

    // Tag the signup with the size they wanted
    const tags = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-rs-tags]'));
    if (tags) {
      const base = `restock,restock-${this.dataset.handle || ''}`;
      const slug = size.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      tags.value = slug ? `${base},restock-${this.dataset.handle || ''}-${slug}` : base;
    }

    // Toggle copy + auto-open when the picked size is sold out
    const plain = this.querySelector('[data-rs-summary-default]');
    const sized = this.querySelector('[data-rs-summary-size]');
    if (plain instanceof HTMLElement && sized instanceof HTMLElement) {
      if (soldOut && size && !allSoldOut) {
        sized.textContent = (sized.dataset.template ??= sized.textContent || '').replace('[size]', size);
        sized.hidden = false;
        plain.hidden = true;
      } else {
        sized.hidden = true;
        plain.hidden = false;
      }
    }

    const details = this.querySelector('details');
    if (details instanceof HTMLDetailsElement && soldOut && !this.hasAttribute('data-done')) details.open = true;
  }
}

/* ------------------------------------------------------------------ */
/* Welcome popup                                                       */
/* ------------------------------------------------------------------ */

class MuWelcomePopup extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {number | undefined} */
  #timer;
  #opened = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const dialog = this.#dialog;
    if (!dialog) return;

    for (const closer of this.querySelectorAll('[data-mu-close]')) {
      closer.addEventListener('click', () => this.close(), { signal });
    }
    dialog.addEventListener('close', () => this.#remember(), { signal });
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === dialog) this.close();
      },
      { signal }
    );

    const params = new URLSearchParams(window.location.search);
    const posted = params.get('customer_posted') === 'true';

    // A restock-alert signup returns here too; that success belongs to the restock block, not us.
    if (params.has('restock')) return;

    if (posted) {
      this.open();
      return;
    }

    if (readCookie(COOKIE_NAME)) return;
    if (params.has('discount')) return; // ad link already carries a code
    if (/\/(cart|checkouts?)\b/.test(window.location.pathname)) return;
    if (window.Shopify?.designMode) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = window.setTimeout(() => this.open(), delay);

    if (this.hasAttribute('data-exit-intent') && window.matchMedia('(hover: hover)').matches) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 0) this.open();
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#abort?.abort();
    this.#abort = null;
  }

  get #dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
  }

  open() {
    const dialog = this.#dialog;
    if (!dialog || this.#opened || dialog.open) return;
    if (document.querySelector('dialog[open]:not(.mu-popup)')) return; // never stack on the cart drawer
    this.#opened = true;
    window.clearTimeout(this.#timer);
    dialog.showModal();
    const email = dialog.querySelector('input[type="email"]');
    if (email instanceof HTMLElement && window.matchMedia('(hover: hover)').matches) email.focus();
  }

  close() {
    const dialog = this.#dialog;
    if (dialog?.open) dialog.close();
  }

  #remember() {
    const days = Math.max(1, Number(this.dataset.cookieDays) || 14);
    writeCookie(COOKIE_NAME, '1', days);
  }
}

if (!customElements.get('mu-upsell-item')) customElements.define('mu-upsell-item', MuUpsellItem);
if (!customElements.get('mu-card-buy')) customElements.define('mu-card-buy', MuCardBuy);
if (!customElements.get('mu-bundle-tiers')) customElements.define('mu-bundle-tiers', MuBundleTiers);
if (!customElements.get('mu-restock')) customElements.define('mu-restock', MuRestock);
if (!customElements.get('mu-welcome-popup')) customElements.define('mu-welcome-popup', MuWelcomePopup);
